import React, { useState, useMemo } from 'react';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import DashboardCardHeader from '../../components/ui/DashboardCardHeader';
import FailingPaymentsIcon from '../../components/ui/icons/FailingPaymentsIcon';
import { 
	FiCreditCard, 
	FiAlertTriangle, 
	FiRefreshCw, 
	FiX, 
	FiDollarSign,
	FiClock 
} from 'react-icons/fi';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import SublyApi from '../../helpers/Api';
import { getAllSubs } from '../../store/user';
import { useToast } from '../../components/ui/toast';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

const FailingPaymentsCard = ({ failingPayments = [], isLoading = false }) => {
	const [processingPayments, setProcessingPayments] = useState(new Set());
	const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, payment: null });
	const dispatch = useDispatch();
	const token = useSelector((state) => state.admin.token);
	const { addToast } = useToast();

	// Calculate revenue impact
	const revenueImpact = failingPayments.reduce((total, payment) => total + payment.amount, 0);

	// Get status color and icon based on days since failure
	const getStatusDisplay = (daysSince, attempts) => {
		if (daysSince <= 2) {
			return {
				color: 'bg-yellow-100 text-yellow-800',
				icon: <FiClock className="h-3 w-3 mr-1" />,
				label: 'Recent'
			};
		} else if (daysSince <= 7) {
			return {
				color: 'bg-orange-100 text-orange-800',
				icon: <FiAlertTriangle className="h-3 w-3 mr-1" />,
				label: 'Urgent'
			};
		} else {
			return {
				color: 'bg-red-100 text-red-800',
				icon: <FiAlertTriangle className="h-3 w-3 mr-1" />,
				label: 'Critical'
			};
		}
	};

	// Handle retry payment
	const handleRetryPayment = async (payment) => {
		if (!payment.invoiceId) {
			addToast({
				title: 'Error',
				description: 'No invoice ID found for this payment',
				type: 'error',
			});
			return;
		}
		
		setProcessingPayments(prev => new Set(prev).add(payment.id));
		
		try {
			const result = await SublyApi.retryPayment(token, payment.invoiceId);
			
			if (result.success) {
				addToast({
					title: 'Success',
					description: `Payment retry successful for ${payment.clientName}`,
					type: 'success',
				});
				// Refresh subscription data to update the display
				dispatch(getAllSubs({ token }));
			} else {
				addToast({
					title: 'Payment Retry Failed',
					description: result.error || 'Unknown error',
					type: 'error',
				});
			}
		} catch (error) {
			console.error('Error retrying payment:', error);
			// Handle both Error objects and string/array errors from API
			const errorMessage = typeof error === 'string'
				? error
				: Array.isArray(error)
					? error[0]
					: error?.message || 'Unknown error';
			addToast({
				title: 'Error',
				description: `Failed to retry payment: ${errorMessage}`,
				type: 'error',
			});
		} finally {
			setProcessingPayments(prev => {
				const updated = new Set(prev);
				updated.delete(payment.id);
				return updated;
			});
		}
	};

	// Handle cancel subscription
	const handleCancelSubscription = (payment) => {
		if (!payment.subscriptionId) {
			addToast({
				title: 'Error',
				description: 'No subscription ID found for this payment',
				type: 'error',
			});
			return;
		}
		
		// Open confirmation modal instead of window.confirm
		setConfirmationModal({ isOpen: true, payment });
	};

	// Confirm and execute subscription cancellation
	const confirmCancelSubscription = async (payment) => {
		
		setProcessingPayments(prev => new Set(prev).add(payment.id));
		
		try {
			const result = await SublyApi.cancelSubscription(token, payment.subscriptionId, false);
			
			if (result.success) {
				addToast({
					title: 'Success',
					description: `Subscription cancelled for ${payment.clientName}`,
					type: 'success',
				});
				// Refresh subscription data to update the display
				dispatch(getAllSubs({ token }));
			} else {
				addToast({
					title: 'Cancellation Failed',
					description: result.error || 'Unknown error',
					type: 'error',
				});
			}
		} catch (error) {
			console.error('Error cancelling subscription:', error);
			// Handle both Error objects and string/array errors from API
			const errorMessage = typeof error === 'string'
				? error
				: Array.isArray(error)
					? error[0]
					: error?.message || 'Unknown error';
			addToast({
				title: 'Error',
				description: `Failed to cancel subscription: ${errorMessage}`,
				type: 'error',
			});
		} finally {
			setProcessingPayments(prev => {
				const updated = new Set(prev);
				updated.delete(payment.id);
				return updated;
			});
		}
	};


	if (isLoading) {
		return (
			<Card className="overflow-hidden">
				<div className="p-4 sm:p-6">
					<DashboardCardHeader
						title="Failing Payments"
						value="Loading..."
						icon={
							<FailingPaymentsIcon 
								className="h-7 w-7 text-clientBlack" 
								failureCount={0}
								showPinkAccent={false}
							/>
						}
					/>
					
					<div className="text-center py-8">
						<FiRefreshCw className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
						<p className="text-sm font-montserrat-regular text-gray-500">
							Loading payment data...
						</p>
					</div>
				</div>
			</Card>
		);
	}

	if (failingPayments.length === 0) {
		return (
			<Card className="overflow-hidden">
				<div className="p-4 sm:p-6">
					<DashboardCardHeader
						title="Failing Payments"
						value="No Issues"
						icon={
							<FailingPaymentsIcon 
								className="h-7 w-7 text-clientBlack" 
								failureCount={0}
								showPinkAccent={false}
							/>
						}
					/>
					
					<div className="text-center py-8">
						<FiCreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
						<p className="text-sm font-montserrat-regular text-gray-500">
							All payments are processing successfully
						</p>
					</div>
				</div>
			</Card>
		);
	}

	return (
		<>
			<Card className="overflow-hidden" role="region" aria-label="Failing Payments Dashboard">
			<div className="p-4 sm:p-6">
				{/* Header Section */}
				<DashboardCardHeader
					title="Failing Payments"
					value={`${failingPayments.length} Issue${failingPayments.length !== 1 ? 's' : ''}`}
					icon={
						<FailingPaymentsIcon 
							className="h-7 w-7 text-clientBlack" 
							failureCount={failingPayments.length}
							showPinkAccent={true}
						/>
					}
				/>

				{/* Refined Revenue Impact */}
				<div className="flex items-center space-x-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
					<FiDollarSign className="h-4 w-4 text-red-600" />
					<div>
						<span className="text-sm font-montserrat-medium text-red-700 block">
							Revenue at Risk
						</span>
						<span className="text-lg font-glacial text-red-800">
							${revenueImpact.toFixed(2)}
						</span>
					</div>
				</div>

				{/* Payments Table */}
				<div className="space-y-4" role="list" aria-label="Failing payments list">
					{failingPayments.map((payment) => {
						const status = getStatusDisplay(payment.daysSinceFailure, payment.attempts);
						const isProcessing = processingPayments.has(payment.id);
						
						return (
							<div
								key={payment.id}
								className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
								role="listitem"
								aria-label={`Payment failure for ${payment.clientName}, amount $${payment.amount.toFixed(2)}, reason: ${payment.reason}`}>
								
								{/* Mobile-First Layout */}
								<div className="space-y-3">
									{/* Top Row: Client info and status */}
									<div className="flex justify-between items-start">
										<div className="flex-1 min-w-0">
											<h4 className="text-sm font-montserrat-medium text-gray-900 break-words">
												{payment.clientName}
											</h4>
											<p className="text-xs font-montserrat-regular text-gray-500 break-words">
												{payment.clientEmail}
											</p>
										</div>
										<span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-montserrat-medium ${status.color}`}>
											{status.icon}
											{status.label}
										</span>
									</div>

									{/* Middle Row: Payment details */}
									<div className="grid grid-cols-2 gap-4 text-xs">
										<div>
											<span className="font-montserrat-regular text-gray-500">Amount</span>
											<p className="font-montserrat-medium text-gray-900">${payment.amount.toFixed(2)}</p>
										</div>
										<div>
											<span className="font-montserrat-regular text-gray-500">Reason</span>
											<p className="font-montserrat-medium text-gray-900">{payment.reason}</p>
										</div>
										<div>
											<span className="font-montserrat-regular text-gray-500">Attempts</span>
											<p className="font-montserrat-medium text-gray-900">{payment.attempts}</p>
										</div>
										<div>
											<span className="font-montserrat-regular text-gray-500">Days Ago</span>
											<p className="font-montserrat-medium text-gray-900">{payment.daysSinceFailure}</p>
										</div>
									</div>

									{/* Bottom Row: Action buttons */}
									<div className="flex flex-col sm:flex-row gap-2 pt-2">
										<Button
											variant="accent"
											size="sm"
											onClick={() => handleRetryPayment(payment)}
											disabled={isProcessing}
											className="flex-1 text-xs min-h-[44px] sm:min-h-0"
											aria-label={`Retry payment for ${payment.clientName}`}>
											{isProcessing ? (
												<>
													<FiRefreshCw className="h-3 w-3 mr-1 animate-spin" />
													Processing...
												</>
											) : (
												<>
													<FiRefreshCw className="h-3 w-3 mr-1" />
													Retry
												</>
											)}
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => handleCancelSubscription(payment)}
											disabled={isProcessing}
											className="flex-1 text-xs min-h-[44px] sm:min-h-0"
											aria-label={`Cancel subscription for ${payment.clientName}`}>
											<FiX className="h-3 w-3 mr-1" />
											Cancel
										</Button>
									</div>
								</div>
							</div>
						);
					})}
				</div>

				{/* Footer */}
				<div className="mt-6 pt-4 border-t border-gray-100">
					<div className="flex justify-between items-center text-xs">
						<span className="font-montserrat-regular text-gray-500">
							Last updated: {new Date().toLocaleDateString()}
						</span>
						<span className="font-montserrat-medium text-gray-600">
							{failingPayments.filter(p => p.daysSinceFailure <= 2).length} recent failures
						</span>
					</div>
				</div>
			</div>
		</Card>
		
		{/* Confirmation Modal for Subscription Cancellation */}
		<ConfirmationModal
			isOpen={confirmationModal.isOpen}
			onClose={() => setConfirmationModal({ isOpen: false, payment: null })}
			onConfirm={() => confirmCancelSubscription(confirmationModal.payment)}
			title="Cancel Subscription"
			message={`Are you sure you want to cancel the subscription for ${confirmationModal.payment?.clientName}? This action cannot be undone and will immediately terminate their access.`}
			confirmText="Cancel Subscription"
			cancelText="Keep Subscription"
			variant="danger"
		/>
		</>
	);
};

FailingPaymentsCard.propTypes = {
	failingPayments: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.string.isRequired,
		clientName: PropTypes.string.isRequired,
		clientEmail: PropTypes.string.isRequired,
		amount: PropTypes.number.isRequired,
		reason: PropTypes.string.isRequired,
		attempts: PropTypes.number.isRequired,
		daysSinceFailure: PropTypes.number.isRequired,
		lastAttempt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
		subscriptionId: PropTypes.string.isRequired,
		invoiceId: PropTypes.string,
		userId: PropTypes.string
	})),
	isLoading: PropTypes.bool
};

export default FailingPaymentsCard;